import React, { useState } from 'react';
import { Search, Filter, Plus, Download, Edit, Trash2, Eye, ChevronDown, Calendar, DollarSign } from 'lucide-react';

interface Opportunity {
  id: string;
  name: string;
  company: string;
  stage: 'Prospecting' | 'Qualification' | 'Proposal' | 'Negotiation' | 'Closed Won' | 'Closed Lost';
  value: number;
  probability: number;
  closeDate: string;
  owner: string;
  createdAt: string;
  lastActivity: string;
}

const mockOpportunities: Opportunity[] = [
  {
    id: '1',
    name: 'Enterprise Software License',
    company: 'Acme Corp',
    stage: 'Negotiation',
    value: 125000,
    probability: 85,
    closeDate: '2024-02-15',
    owner: 'John Sales',
    createdAt: '2024-01-10',
    lastActivity: '2024-01-20'
  },
  {
    id: '2',
    name: 'Cloud Migration Project',
    company: 'Tech Solutions',
    stage: 'Proposal',
    value: 85000,
    probability: 60,
    closeDate: '2024-03-01',
    owner: 'Sarah Manager',
    createdAt: '2024-01-08',
    lastActivity: '2024-01-19'
  },
  {
    id: '3',
    name: 'Security Consultation',
    company: 'Global Industries',
    stage: 'Qualification',
    value: 45000,
    probability: 40,
    closeDate: '2024-02-28',
    owner: 'Mike Rep',
    createdAt: '2024-01-12',
    lastActivity: '2024-01-18'
  },
  {
    id: '4',
    name: 'Data Analytics Platform',
    company: 'StartupXYZ',
    stage: 'Prospecting',
    value: 75000,
    probability: 25,
    closeDate: '2024-04-15',
    owner: 'Lisa Sales',
    createdAt: '2024-01-15',
    lastActivity: '2024-01-16'
  },
];

export const OpportunitiesCRUDInterface: React.FC = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>(mockOpportunities);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredOpportunities = opportunities.filter(opp => {
    const matchesSearch = opp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         opp.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         opp.owner.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = stageFilter === 'all' || opp.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  const totalPages = Math.ceil(filteredOpportunities.length / itemsPerPage);
  const paginatedOpportunities = filteredOpportunities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Prospecting':
        return 'bg-gray-100 text-gray-800';
      case 'Qualification':
        return 'bg-netlify-teal-100 text-netlify-teal-800';
      case 'Proposal':
        return 'bg-netlify-coral-100 text-netlify-coral-800';
      case 'Negotiation':
        return 'bg-netlify-coral-200 text-netlify-coral-900';
      case 'Closed Won':
        return 'bg-netlify-teal-200 text-netlify-teal-900';
      case 'Closed Lost':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProbabilityColor = (probability: number) => {
    if (probability >= 75) return 'text-netlify-teal-600';
    if (probability >= 50) return 'text-netlify-coral-500';
    if (probability >= 25) return 'text-netlify-coral-600';
    return 'text-red-600';
  };

  const handleDelete = (id: string) => {
    setOpportunities(opportunities.filter(opp => opp.id !== id));
  };

  const totalValue = filteredOpportunities.reduce((sum, opp) => sum + opp.value, 0);
  const weightedValue = filteredOpportunities.reduce((sum, opp) => sum + (opp.value * opp.probability / 100), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Opportunities</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and manage your sales opportunities through the pipeline
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button className="inline-flex items-center px-4 py-2 border border-netlify-teal-300 rounded-lg shadow-sm text-sm font-medium text-netlify-teal-700 bg-white hover:bg-netlify-teal-50 transition-all duration-200">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button className="netlify-button-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Opportunity
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-r from-netlify-teal-50 to-netlify-teal-100 rounded-lg p-4">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-netlify-primary" />
            <div className="ml-3">
              <p className="text-sm font-medium text-netlify-secondary">Total Pipeline</p>
              <p className="text-2xl font-bold text-netlify-secondary">${totalValue.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-netlify-coral-50 to-netlify-coral-100 rounded-lg p-4">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-netlify-accent" />
            <div className="ml-3">
              <p className="text-sm font-medium text-netlify-coral-700">Weighted Pipeline</p>
              <p className="text-2xl font-bold text-netlify-coral-800">${weightedValue.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-netlify-teal-50 to-netlify-coral-50 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-netlify-teal-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-netlify-secondary">Active Opportunities</p>
              <p className="text-2xl font-bold text-netlify-secondary">{filteredOpportunities.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-netlify-primary focus:border-netlify-primary"
            placeholder="Search opportunities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Stage Filter */}
        <div className="relative">
          <select
            className="appearance-none bg-white border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-netlify-primary focus:border-netlify-primary"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
          >
            <option value="all">All Stages</option>
            <option value="Prospecting">Prospecting</option>
            <option value="Qualification">Qualification</option>
            <option value="Proposal">Proposal</option>
            <option value="Negotiation">Negotiation</option>
            <option value="Closed Won">Closed Won</option>
            <option value="Closed Lost">Closed Lost</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Additional Filters */}
        <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <Filter className="w-4 h-4 mr-2" />
          More Filters
        </button>
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-sm text-gray-700">
          Showing {paginatedOpportunities.length} of {filteredOpportunities.length} opportunities
        </p>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Opportunity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Probability
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Close Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedOpportunities.map((opp) => (
              <tr key={opp.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{opp.name}</div>
                    <div className="text-sm text-gray-500">{opp.company}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opp.stage)}`}>
                    {opp.stage}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">${opp.value.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">
                    Weighted: ${Math.round(opp.value * opp.probability / 100).toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-netlify-primary h-2 rounded-full"
                        style={{ width: `${opp.probability}%` }}
                      ></div>
                    </div>
                    <span className={`text-sm font-medium ${getProbabilityColor(opp.probability)}`}>
                      {opp.probability}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(opp.closeDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-700">
                        {opp.owner.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <span className="ml-2 text-sm text-gray-900">{opp.owner}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button className="text-netlify-primary hover:text-netlify-teal-700">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="text-gray-600 hover:text-gray-900">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(opp.id)}
                      className="text-netlify-accent hover:text-netlify-coral-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-netlify-teal-50 border-netlify-primary text-netlify-primary'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesCRUDInterface;